<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditDeleteButtons from '../widgets/EditDeleteButtons.vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import type { CustomFieldDefinition, Asset, Response, SubAsset } from '@/models/api-generated'
import { assetRefreshCallback } from '@/components/business_processes/shared'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import { PermAllAssetDelete, PermAllAssetWrite, PermOwnAssetDelete, PermOwnAssetWrite } from '@/models/enums-generated'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import CustomValueDisplay from '../custom_field/CustomValueDisplay.vue'
import ResponsibleSelect from '../widgets/ResponsibleSelect.vue'
import { hasPermissionOwn } from '@/services/utils'
import NumericInput from '../widgets/NumericInput.vue'

const props = defineProps<{
	asset: Asset
	subAssetId: number
}>()
const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const customValues = ref<CustomValueMap>()
const isAdd = computed(() => props.subAssetId === 0)
const isEdit = ref(false)
const model = ref<SubAsset>(defaultAsset())
const isChangeMode = computed(() => isAdd.value || isEdit.value)
const canEdit = computed(() => hasPermissionOwn(model.value.responsible, PermAllAssetWrite, PermOwnAssetWrite))
const canDelete = computed(() => hasPermissionOwn(model.value.responsible, PermAllAssetDelete, PermOwnAssetDelete))
const isValidName = computed(() => model.value.name.trim().length > 0)
const canSave = computed(() => isValidName.value)
const custom = ref<CustomFieldDefinition[]>([])

onMounted(async () => {
	await refresh()
	slider.watchChanges(model)
})

watch(() => props.subAssetId, refresh)
watch(isEdit, (edit) => slider.setFooter(edit ? editBtns : viewBtns))

const editBtns = <CancelSaveButtons saveId="save-sub-asset" cancelId="cancel-sub-asset" canSave={canSave} onCancel={cancel} onSave={save} />
const viewBtns = (
	<EditDeleteButtons
		editId="open-edit-sub-asset"
		deleteId="delete-sub-asset"
		canEdit={canEdit}
		onEdit={edit}
		canDelete={canDelete}
		onDelete={remove}
	/>
)

async function edit() {
	isEdit.value = true
}

async function refresh() {
	isEdit.value = false
	if (!isAdd.value) {
		const { data } = await http.get<Response<SubAsset>>(`/api/asset/sub/${props.subAssetId}`)
		if (data.data) model.value = data.data
		slider.setFooter(viewBtns)
	} else {
		model.value = defaultAsset()
		slider.setFooter(editBtns)
	}
	await getCustomFields()
}

async function getCustomFields() {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/sub_assets`)
	if (data.data?.length) custom.value.push(...data.data)

	if (!isAdd.value) customValues.value = await fetchCustomValues('sub_assets', props.subAssetId)
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

function remove() {
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'subAsset', confirmDelete)
}

async function confirmDelete() {
	const { status } = await http.delete(`/api/asset/sub/${model.value.id}`)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyDeleted'))
		slider.setUnsavedChanges(false)
		slider.close()
		assetRefreshCallback.value?.forEach((fn) => fn())
	}
}

async function save() {
	model.value.customFields = customValues.value ?? {}
	const { status } = await http.post('/api/asset/sub', model.value)
	if (status === 200) {
		toaster.showSuccess(t(isAdd.value ? 'misc.successfullyAdded' : 'misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		assetRefreshCallback.value?.forEach((fn) => fn())
	}
}

function updateCustomValue(fieldId: number, value: string) {
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][model.value.id] = value
	model.value.customFields ||= {}
	model.value.customFields[fieldId] ??= {}
	model.value.customFields[fieldId][model.value.id] = value
}

function defaultAsset(): SubAsset {
	return {
		assetId: props.asset.id,
		id: 0,
		inventoryNr: '',
		name: '',
		location: '',
		price: 0,
		comment: '',
		active: true,
		customFields: {},
	}
}
</script>

<template>
	<div :class="{ striped: !isChangeMode }">
		<div class="row">
			<label class="col-4 col-form-label">{{ t('asset.inventoryNr') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<input v-if="isChangeMode" id="asset-inventory-nr" class="form-control mb-2" v-model="model.inventoryNr" required />
				<span v-else class="text-end">{{ model.inventoryNr || '-' }}</span>
			</div>
		</div>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('misc.name') }}</label>
			<div class="col-8 d-flex flex-column">
				<input
					v-if="isChangeMode"
					id="asset-name"
					class="form-control mb-1 w-100"
					v-model="model.name"
					:class="{ 'is-invalid': !isValidName }"
					required
				/>
				<div v-if="isChangeMode" class="invalid-feedback">{{ $t('misc.validName') }}</div>
				<div v-else class="d-flex align-items-center justify-content-end flex-grow-1">
					<span class="text-end">{{ model.name || '-' }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('responsible') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<ResponsibleSelect v-if="isChangeMode" v-model="model.responsible" id="asset-responsible" class="mb-2"></ResponsibleSelect>
				<span v-else class="text-end">{{ model.responsible?.label || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('location') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<input v-if="isChangeMode" class="form-control mb-2" v-model="model.location" id="asset-location" />
				<span v-else class="text-end">{{ model.location || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label class="col-4 col-form-label">{{ t('price') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<NumericInput v-if="isChangeMode" id="asset-price" class="form-control input-box mb-1" v-model="model.price"></NumericInput>
				<span v-else class="text-end">{{ model.price || '-' }}</span>
			</div>
		</div>

		<div class="row">
			<label for="asset-comment" class="col-4 col-form-label">{{ t('misc.comment') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<textarea
					v-if="isChangeMode"
					v-model="model.comment"
					class="form-control w-100 mb-1"
					name="comment"
					id="asset-comment"
					maxlength="400"
				></textarea>
				<span v-else class="text-end w-100">{{ model.comment || '-' }}</span>
			</div>
		</div>

		<template v-if="!isChangeMode">
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueDisplay :field="field" :value="customValues?.[field.id]?.[model.id] ?? ''" />
					</div>
				</div>
			</template>
		</template>
		<template v-else>
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueEdit
							:field="field"
							:modelValue="customValues?.[field.id]?.[model.id] ?? ''"
							@update:modelValue="(val) => updateCustomValue(field.id, val ?? '')"
						/>
					</div>
				</div>
			</template>
		</template>
		<div class="row">
			<label class="col-4 col-form-label">{{ t('status.label') }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<div v-if="isChangeMode" class="form-check form-switch">
					<input id="status" type="checkbox" class="form-check-input" v-model="model.active" />
				</div>
				<div v-else>
					<i v-if="model.active" class="bi bi-circle-fill status-normal-background"></i>
					<i v-else class="bi bi-circle-fill status-verybig-background"></i>
				</div>
			</div>
		</div>
	</div>
</template>
